import { PlantTagRecord } from "@/lib/algolia/plantTags";
import { RichSBData } from "@/components/ProductPage/ProductPageHero/types";
import ProductPageHero from "@/components/ProductPage/ProductPageHero";
import { useRouter } from "next/router";
// Type definition for redirect entries
interface ManualRedirect {
  source: string;
  destination: string;
  permanent: boolean;
}

// Use require to import JSON file
const manualRedirects: ManualRedirect[] = require("../../../../manual-redirects.json");

import styled from "styled-components";
import ContentWrapper from "@/components/Core/ContentWrapper";

const RedirectWarningContainer = styled.div`
  background-color: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  margin-left: 8px;
  margin-right: 8px;
  margin-top: 32px;
  color: #991b1b;
  font-family:
    -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans",
    "Helvetica Neue", sans-serif;
  @media (max-width: 1100px) {
    margin-top: 72px;
  }
`;

const RedirectWarningTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
`;

const RedirectWarningText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
`;

const RedirectWarningLink = styled.a`
  color: #991b1b;
  text-decoration: underline;
  font-weight: 500;

  &:hover {
    text-decoration: none;
  }
`;

export interface StoryblokDataLakeItemProps {
  blok: {
    _uid: string;
    component: string;
    PlantPlan_Code: string;
    displayName: string;
    description: any;
    gallery: any[];
    resources: any[];
    infoItems: any[];
    [key: string]: any;
    algoliaRecord: PlantTagRecord;
  };
}

// Component to check for redirects and display warning
const RedirectWarning = () => {
  const router = useRouter();
  const currentPath = router.asPath.split("?")[0];

  // Find if current path matches any redirect source
  const matchingRedirect = manualRedirects.find(
    (redirect) => redirect.source === currentPath || redirect.source === currentPath.replace(/^\//, ""),
  );

  if (!matchingRedirect) {
    console.log("No matching redirect found for path:", currentPath);
    return null;
  }

  return (
    <RedirectWarningContainer>
      <RedirectWarningTitle>⚠️ Page Redirect Notice</RedirectWarningTitle>
      <RedirectWarningText>
        This page has a manual redirect hard-coded into the site, going to :{" "}
        <RedirectWarningLink href={matchingRedirect.destination}>{matchingRedirect.destination}</RedirectWarningLink>
        {matchingRedirect.permanent ? " (permanent redirect)" : " (temporary redirect)"}
      </RedirectWarningText>
    </RedirectWarningContainer>
  );
};

const StoryblokDataLakeItem = ({ blok }: StoryblokDataLakeItemProps) => {
  if (!blok.algoliaRecord) {
    console.warn(`No Algolia record provided for PlantPlan_Code: ${blok.PlantPlan_Code}`);
    return (
      <div className='error-message'>
        <p>Product data not found for code: {blok.PlantPlan_Code}</p>
      </div>
    );
  }

  // Transform Storyblok data to match RichSBData interface
  const richSBData: RichSBData = {
    content: {
      _uid: blok._uid,
      component: blok.component,
      description: blok.description,
      displayName: blok.displayName,
      gallery: blok.gallery || [],
      infoItems: blok.infoItems || [],
      resources: blok.resources || [],
      PlantPlan_Code: blok.PlantPlan_Code,
    },
  };

  return (
    <>
      {process.env.NEXT_PUBLIC_PRODUCTION_SITE !== "true" && (
        <ContentWrapper>
          <RedirectWarning />
        </ContentWrapper>
      )}
      <ProductPageHero record={blok.algoliaRecord} RichSBData={richSBData} />
    </>
  );
};

export default StoryblokDataLakeItem;
